/**
 * ProtocolDetail — a study's documents, read from the platform registry (eReg).
 *
 * P3 (source-of-truth plan §5.2): protocol-scoped documents live in eReg and
 * render via the shared StudyDocumentsCard (slots, versions, extraction state,
 * View); uploads are eReg deep links that return here. Files uploaded to
 * Intelligence before P3 stay listed below (manageable until they're adopted
 * into eReg via Registry adoption); registry-projection rows are excluded from
 * that table — the card above is their real home.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileText, Trash2, RefreshCw, CheckCircle, XCircle,
  Loader2, FolderOpen, Building2, MapPin, Filter, Pencil
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { Protocol, IntelligenceDocument } from '../types';
import { DOCUMENT_TYPES, isRegistryProjection } from '../types';
import { fieldCls, StudyDocumentsCard } from '../../ui';
import { eregStudyUploadLink } from '../../ui/returnTo';

export function ProtocolDetail() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [protocol, setProtocol] = useState<Protocol | null>(null);
  const [documents, setDocuments] = useState<IntelligenceDocument[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedType, setSelectedType] = useState<string>('all');

  const isAdmin = ['super_admin', 'administrator'].includes(user?.role || '');
  const returnTo = location.pathname;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [protocolsRes, docsRes] = await Promise.all([
        api.get('/api/intelligence/protocols', { params: { all: 'true' } }),
        api.get(`/api/intelligence/protocols/${protocolId}/documents`),
      ]);

      const foundProtocol = (protocolsRes.data || []).find((p: Protocol) => p.id === protocolId);
      setProtocol(foundProtocol || null);
      setDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  useEffect(() => {
    if (protocolId) {
      loadData();
    }
  }, [protocolId, loadData]);

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/api/intelligence/documents/${docId}`);
      await loadData();
    } catch (error: any) {
      console.error('Error deleting:', error);
      alert(error.response?.data?.error || 'Failed to delete document.');
    }
  };

  const handleReprocess = async (docId: string) => {
    try {
      await api.post(`/api/intelligence/documents/${docId}/reprocess`);
      await loadData();
    } catch (error) {
      console.error('Error reprocessing:', error);
      alert('Failed to reprocess document.');
    }
  };

  const handleRename = async (doc: IntelligenceDocument) => {
    const current = doc.title || doc.filename;
    const next = window.prompt('Rename document', current);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;

    try {
      await api.patch(`/api/intelligence/documents/${doc.id}`, { title: trimmed });
      await loadData();
    } catch (error: any) {
      console.error('Error renaming document:', error);
      alert(error.response?.data?.error || 'Failed to rename document.');
    }
  };

  const handleUpdateDocType = async (docId: string, newType: string) => {
    try {
      await api.patch(`/api/intelligence/documents/${docId}`, {
        document_type: newType
      });
      await loadData();
    } catch (error: any) {
      console.error('Error updating document type:', error);
      alert(error.response?.data?.error || 'Failed to update document type.');
    }
  };

  const getDocTypeLabel = (type: string | null) => {
    const found = DOCUMENT_TYPES.find(t => t.value === type);
    return found?.label || type || 'Other';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle size={12} />
            Processed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <Loader2 size={12} className="animate-spin" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <XCircle size={12} />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            Pending
          </span>
        );
    }
  };

  // Registry-projection rows render in the StudyDocumentsCard above; this
  // table is for legacy Intelligence uploads only (pre-P3, until adopted).
  const legacyDocuments = documents.filter(doc => !isRegistryProjection(doc));

  const filteredDocuments = legacyDocuments.filter(doc =>
    selectedType === 'all' || doc.document_type === selectedType
  );

  // Group documents by type
  const groupedDocuments = filteredDocuments.reduce((acc, doc) => {
    const type = doc.document_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {} as Record<string, IntelligenceDocument[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="max-w-7xl mx-auto text-center py-12">
        <FolderOpen size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100">Study not found</h2>
        <button
          onClick={() => navigate('/intelligence/protocols')}
          className="text-sm font-medium text-primary-600 transition-colors duration-150 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Back to studies
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/intelligence/protocols')}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:text-gray-900 mb-4 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft size={18} />
          Back to studies
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FolderOpen size={20} className="text-primary-600 dark:text-primary-400" />
              <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">{protocol.protocol_number}</h1>
            </div>
            {protocol.study_nickname && (
              <p className="text-sm text-gray-600 mb-2 dark:text-gray-300">{protocol.study_nickname}</p>
            )}
            {protocol.sponsor_name && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Sponsor: {protocol.sponsor_name}</p>
            )}

            {/* Network/Site labels */}
            <div className="flex flex-wrap gap-2 mt-3">
              {protocol.network_name && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                  <Building2 size={12} />
                  {protocol.network_name}
                </span>
              )}
              {protocol.site_name && (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  <MapPin size={12} />
                  {protocol.site_name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Study documents — read from the registry (eReg) */}
      <div className="space-y-4 mb-6">
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={eregStudyUploadLink(protocol.id, { role: 'protocol', returnTo, returnLabel: 'Site Intelligence' })}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <Upload className="h-4 w-4" /> Upload protocol in eReg
            </Link>
            <Link
              to={eregStudyUploadLink(protocol.id, { role: 'icf', returnTo, returnLabel: 'Site Intelligence' })}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <Upload className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Upload ICF in eReg
            </Link>
          </div>
        )}

        <StudyDocumentsCard
          protocolId={protocol.id}
          canManageInEreg={isAdmin}
          returnTo={returnTo}
          returnLabel="Site Intelligence"
        />
      </div>

      {legacyDocuments.length > 0 && (
        <>
          {/* Document type filter */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Legacy Intelligence uploads</h2>
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className={fieldCls}
              >
                <option value="all">All Types ({legacyDocuments.length})</option>
                {DOCUMENT_TYPES.map(t => {
                  const count = legacyDocuments.filter(d => d.document_type === t.value).length;
                  return count > 0 ? (
                    <option key={t.value} value={t.value}>{t.label} ({count})</option>
                  ) : null;
                })}
              </select>
            </div>
          </div>
          <p className="-mt-2 mb-4 text-xs text-gray-500 dark:text-gray-400">
            Files uploaded before documents moved to eReg. New uploads and versions go to eReg above.
          </p>

          {/* Legacy documents list */}
          <div className="space-y-6">
            {Object.entries(groupedDocuments).sort(([a], [b]) => a.localeCompare(b)).map(([type, docs]) => (
              <div key={type}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 dark:text-gray-400">
                  {getDocTypeLabel(type)}
                  <span className="text-gray-400 font-normal ml-2 dark:text-gray-500">({docs.length})</span>
                </h3>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Document</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Version</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Uploaded</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {docs.map((doc) => (
                        <tr key={doc.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <FileText size={20} className="text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{doc.title || doc.filename}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{doc.filename}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={doc.document_type || 'other'}
                              onChange={(e) => handleUpdateDocType(doc.id, e.target.value)}
                              className={fieldCls}
                            >
                              {DOCUMENT_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-300">v{doc.version}</span>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(doc.processing_status)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isAdmin && (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleRename(doc)}
                                  className="p-1 text-gray-400 transition-colors duration-150 hover:text-primary-600 dark:hover:text-primary-400"
                                  title="Rename"
                                >
                                  <Pencil size={16} />
                                </button>
                                {doc.processing_status === 'failed' && (
                                  <button
                                    onClick={() => handleReprocess(doc.id)}
                                    className="p-1 text-gray-400 transition-colors duration-150 hover:text-blue-600 dark:hover:text-blue-400"
                                    title="Reprocess"
                                  >
                                    <RefreshCw size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(doc.id)}
                                  className="p-1 text-gray-400 transition-colors duration-150 hover:text-red-600 dark:hover:text-red-400"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
